/**
 * Reading a CSV in the browser (KPR-1899): bytes to text, text to rows.
 *
 * A file from Excel on Windows is Windows-1252 unless someone chose otherwise,
 * "Unicode text" is UTF-16 with a mark, and a European Excel separates with a
 * semicolon. Those three are the files people actually have, so they are read
 * without asking. Every cell stays the string the file holds: nothing here
 * interprets a value, so `=1+1` is the five characters it looks like.
 */

/** The file's text. UTF-8 when it is valid UTF-8, else Windows-1252. */
export function decodeText(bytes) {
	const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
	if (data[0] === 0xff && data[1] === 0xfe) return new TextDecoder('utf-16le').decode(data.subarray(2));
	if (data[0] === 0xfe && data[1] === 0xff) return new TextDecoder('utf-16be').decode(data.subarray(2));
	const body = data[0] === 0xef && data[1] === 0xbb && data[2] === 0xbf ? data.subarray(3) : data;
	try {
		return new TextDecoder('utf-8', { fatal: true }).decode(body);
	} catch (e) {
		return new TextDecoder('windows-1252').decode(body);
	}
}

const DELIMITERS = [',', ';', '\t'];

/** Which of comma, semicolon and tab splits the first line most, outside quotes. Comma on a tie. */
export function sniffDelimiter(text) {
	const counts = new Map(DELIMITERS.map(d => [d, 0]));
	let quoted = false;
	for (let i = 0; i < text.length; i += 1) {
		const ch = text[i];
		if (ch === '"') quoted = !quoted;
		else if (!quoted && (ch === '\n' || ch === '\r')) break;
		else if (!quoted && counts.has(ch)) counts.set(ch, counts.get(ch) + 1);
	}
	return DELIMITERS.reduce((best, d) => (counts.get(d) > counts.get(best) ? d : best), ',');
}

/**
 * RFC 4180: quotes wrap a field, a doubled quote is one quote, and a quoted
 * field may hold the delimiter and line breaks. A quote that opens mid-field
 * is an ordinary character, as a spreadsheet reads it.
 *
 * -> string[][], one entry per line of the file, blank lines included: a row's
 * place in this list is the line a person finds it on.
 */
export function parseDelimited(text, delimiter = ',') {
	const rows = [];
	let row = [];
	let field = '';
	let quoted = false;
	const endField = () => { row.push(field); field = ''; };
	const endRow = () => { endField(); rows.push(row); row = []; };
	for (let i = 0; i < text.length; i += 1) {
		const ch = text[i];
		if (quoted) {
			if (ch !== '"') field += ch;
			else if (text[i + 1] === '"') { field += '"'; i += 1; } else quoted = false;
		} else if (ch === '"' && field === '') {
			quoted = true;
		} else if (ch === delimiter) {
			endField();
		} else if (ch === '\n') {
			endRow();
		} else if (ch === '\r') {
			if (text[i + 1] === '\n') i += 1;
			endRow();
		} else {
			field += ch;
		}
	}
	if (field !== '' || row.length) endRow();
	return rows;
}

/** -> { rows, delimiter }: the raw rows of a CSV or TSV, and what split them. */
export function readCsv(bytes) {
	const text = decodeText(bytes);
	const delimiter = sniffDelimiter(text);
	return { rows: parseDelimited(text, delimiter), delimiter };
}
